import { MINUTES_PER_DAY } from '../model/constants';
import { addDays } from './date';

export interface RehomedPiece {
  readonly personalDate: string;
  readonly startMin: number;
  readonly endMin: number;
}

/**
 * Re-assign a span to personal days after the day start changed to `newDayStartMin`.
 *
 * The span `[startMin, endMin]` is measured from 00:00 of `personalDate`. Every wall minute `m`
 * belongs to the personal day `personalDate + k` where `k = floor((m - newDayStartMin) / 1440)`;
 * its wall minute relative to that day becomes `m - 1440 * k`. A span crossing a day boundary
 * is cut at the boundary into consecutive pieces, ordered by start. A span that stays within a
 * single personal day yields one piece (possibly moved to another date).
 */
export function rehomeSpan(
  personalDate: string,
  startMin: number,
  endMin: number,
  newDayStartMin: number,
): RehomedPiece[] {
  const pieces: RehomedPiece[] = [];
  let cursor = startMin;
  while (cursor < endMin) {
    const dayOffset = Math.floor((cursor - newDayStartMin) / MINUTES_PER_DAY);
    const dayEnd = newDayStartMin + (dayOffset + 1) * MINUTES_PER_DAY;
    const pieceEnd = Math.min(endMin, dayEnd);
    pieces.push({
      personalDate: addDays(personalDate, dayOffset),
      startMin: cursor - dayOffset * MINUTES_PER_DAY,
      endMin: pieceEnd - dayOffset * MINUTES_PER_DAY,
    });
    cursor = pieceEnd;
  }
  return pieces;
}

/**
 * The personal date a check-in log is filed under after the day start changed from
 * `oldDayStartMin` to `newDayStartMin` (docs/glossary.md "re-homing a check-in log").
 *
 * A log of an item planned at `plannedMin` (wall-clock minutes 0–1439) denotes one occurrence
 * of that planned time, whose absolute time the change must not alter. Under a day start `s`,
 * the occurrence on calendar day `D` belongs to personal day `D - 1` when `plannedMin < s` and
 * to `D` otherwise. So the occurrence's calendar day is `personalDate + 1` when
 * `plannedMin < oldDayStartMin` (otherwise `personalDate`), and the new personal date is that
 * calendar day minus one when `plannedMin < newDayStartMin`. The result is the same for every
 * `personalDate` of one item: all its logs move by the same number of days.
 */
export function rehomeCheckinDate(
  personalDate: string,
  plannedMin: number,
  oldDayStartMin: number,
  newDayStartMin: number,
): string {
  const occurrenceDay = addDays(personalDate, plannedMin < oldDayStartMin ? 1 : 0);
  return addDays(occurrenceDay, plannedMin < newDayStartMin ? -1 : 0);
}
